import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSyncState } from "@/hooks/useSyncState";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dialog, Toast } from "antd-mobile";
import request from "@/network/request";
import {
  CMD_ID_CALL,
  CMD_ID_TRACK,
  HANG_UP,
  START_VIDEO,
  STOP_VIDEO,
  SWITCH_OFF,
  SWITCH_ON,
} from "@/common/cmd";
import { ECallProvider, ERole } from "@/common/enums";
import { addListener, pushHistory, removeListener } from "@/common/utils";
import system from "@/stores/system";
import { CancelTokenSource } from "axios";
import { useRequest } from "ahooks";
import { TCallResponse } from "@/common/typings";
import { createCallAdapter, ICallAdapter } from "../adapters/CallAdapter";

enum ECallStatus {
  isCalling,
  isSpeaking,
  isVideoing,
  hangup,
}

const TOKEN_CANCEL = "用户取消敲门操作";

// 格式化时分秒
const formatTime = (seconds: number) => {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
};

export const useCallAdapter = () => {
  const navigate = useNavigate();
  const adapterRef = useRef<ICallAdapter | null>(null);

  const remoteVideoRef = useRef<HTMLVideoElement | HTMLDivElement | null>(null);
  const remoteVideoFullRef = useRef<HTMLVideoElement | HTMLDivElement | null>(
    null
  );
  const localVideoRef = useRef<HTMLVideoElement | HTMLDivElement | null>(null);

  const [role, setRole] = useState<ERole>(ERole.Guest);
  const [status, setStatus] = useState<ECallStatus>(ECallStatus.isCalling);
  const [isEnteredRoom, setIsEnteredRoom] = useState(false);
  const [remoteUser, setRemoteUser] = useSyncState(undefined);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [callSeconds, setCallSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const cancelToken = useRef<CancelTokenSource | null>(null);
  const [showEmojiPanel, setShowEmojiPanel] = useState(false);
  const [useFrontCamera, setUseFrontCamera] = useState(true);
  const [isTrackOn, setIsTrackOn] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const [searchParams] = useSearchParams();
  const roomId = useRef(searchParams.get("room") || "");
  const roomPwd = useRef(searchParams.get("pwd") || "");

  const back = useCallback(() => {
    system.stopCallAudio();
    setTimeout(() => {
      navigate("/", { replace: true });
    }, 1000);
  }, [navigate]);

  const startCountdown = useCallback(() => {
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          system.stopCallAudio();
          clearInterval(interval);
          adapterRef.current?.startLocalAudio();
          setStatus(ECallStatus.isSpeaking);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const enterRoom = useCallback(
    async (adapter: ICallAdapter, response: TCallResponse, roleRef: ERole) => {
      try {
        if (adapter.getProvider() === ECallProvider.Trtc) {
          await adapter.enterRoom({
            userId: response.trtc?.user_id,
            userSig: response.trtc?.user_sig,
            appId: response.trtc?.app_id,
            roomId: response.trtc?.room_id,
          });
        } else if (adapter.getProvider() === ECallProvider.LiveKit) {
          await adapter.enterRoom({
            url: response.livekit?.url,
            token: response.livekit?.token,
          });
        }

        setIsEnteredRoom(true);

        if (roleRef === ERole.Guest) {
          system.stopCallAudio();
          setStatus(ECallStatus.isVideoing);
          setIsVideoOpen(true);
          await adapter.startLocalAudio();
          setTimeout(() => {
            // 延迟启动视频
            adapter.startLocalVideo({
              view: localVideoRef.current,
              option: { mirror: true },
            });
          }, 1000);
        } else {
          startCountdown();
        }
      } catch (error: any) {
        console.error("Enter room failed:", error);
        Toast.show(`错误: ${error.message}`);
      }
    },
    [startCountdown]
  );

  const handleHangup = useCallback(async () => {
    cancelToken.current?.cancel(TOKEN_CANCEL);
    system.stopCallAudio();
    system.playHangupAudio();
    console.log("[CallAdapter] hangup");

    try {
      setIsVideoOpen(false);
      if (adapterRef.current) {
        await adapterRef.current.exitRoom();
      }
      setRemoteUser(undefined);
      console.log("[CallAdapter] exitRoom success");
      setStatus(ECallStatus.hangup);
      back();
    } catch (err) {
      console.log("[CallAdapter] exitRoom failed:", err);
      back();
    }
  }, [back, setRemoteUser, setIsVideoOpen, setStatus]);

  const installEventListeners = useCallback(
    (adapter: ICallAdapter) => {
      adapter.installEventListeners({
        onRemoteUser: (user?: any) => {
          setRemoteUser(user);
          if (
            (user && remoteVideoRef.current) ||
            (user && user.track.kind === "audio")
          ) {
            adapter.startRemoteVideo({
              userId: user.userId,
              streamType: user.streamType,
              track: user.track,
              view: remoteVideoRef.current,
            });
          }
        },
        onHangup: () => {
          handleHangup();
        },
        onRemoteVideoRef: (ref) => {
          remoteVideoRef.current = ref.current;
        },
      });
    },
    [setRemoteUser, handleHangup]
  );

  const { run: runCall } = useRequest(
    async () =>
      (await request.post(
        "/api/visit/v1/call",
        {
          room_id: Number.parseInt(roomId.current),
          password: roomPwd.current,
        },
        undefined,
        cancelToken.current?.token
      )) as TCallResponse,
    {
      manual: true,
      onSuccess: async (data: TCallResponse) => {
        setRole(data.role);

        // 确定使用哪个 provider
        const provider = data.rtc_provider;

        // 创建适配器
        const adapter = createCallAdapter(provider, data);
        await adapter.initialize();
        adapterRef.current = adapter;

        // 安装事件监听器
        installEventListeners(adapter);

        // 进入房间
        await enterRoom(adapter, data, data.role);
      },
      onError: (error) => {
        console.error("Error knocking on the door:", error);

        const isTokenCanceled = (error as any).error === TOKEN_CANCEL;
        const content = isTokenCanceled
          ? "呼叫已经取消"
          : "敲门失败，" + (error as any).message;
        Toast.show({
          content,
          icon: "fail",
          afterClose: isTokenCanceled
            ? undefined
            : () => {
                back();
              },
        });

        system.stopCallAudio();
        system.playHangupAudio();
      },
    }
  );

  const calling = useCallback(async () => {
    // setStatus(ECallStatus.isCalling);
    // setRole(ERole.Host);
    // // 创建适配器
    // const response = {
    //   guest_id: 1,
    //   role: ERole.Host,
    //   rtc_provider: ECallProvider.LiveKit,
    //   livekit: {
    //     url: "wss://v-1.criss.com.cn",
    //     token:
    //       "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3OTc1NjE0MzUsImlkZW50aXR5IjoidXNlci0wMSIsImlzcyI6IkFQSVk3c0J6VU1XMk5yRiIsIm5iZiI6MTc2NjAyNTQzNSwic3ViIjoidXNlci0wMSIsInZpZGVvIjp7InJvb20iOiJteS1yb29tIiwicm9vbUpvaW4iOnRydWV9fQ.DegxHNCgnXzRodrb4wkOOq-nTXbvSH_HGkuSjDOdUuo",
    //   },
    // };
    // const adapter = createCallAdapter(ECallProvider.LiveKit, response);
    // await adapter.initialize();
    // adapterRef.current = adapter;

    // // 安装事件监听器
    // installEventListeners(adapter);
    // enterRoom(adapter, response, ERole.Host);

    setStatus(ECallStatus.isCalling);
    runCall();
  }, [runCall]);

  const handleVideoToggle = useCallback(async () => {
    if (!adapterRef.current) return;

    if (isVideoOpen) {
      await adapterRef.current.sendMessage(CMD_ID_CALL, STOP_VIDEO);
      await adapterRef.current.stopLocalVideo();
    } else {
      if (status !== ECallStatus.isVideoing) {
        setStatus(ECallStatus.isVideoing);
        try {
          setTimeout(() => {
            adapterRef.current?.startLocalVideo({
              view: localVideoRef.current,
            });
          }, 1000); // 等待1秒后再打开视频
        } catch (error) {
          console.error("[VideoRoom] startLocalVideo failed:", error);
        }
      }
      await adapterRef.current.sendMessage(CMD_ID_CALL, START_VIDEO);
      // 视频启动逻辑在具体实现中处理
    }

    setIsVideoOpen(!isVideoOpen);
  }, [isVideoOpen, status]);

  const handleHangupClick = useCallback(async () => {
    console.log("=============> handleHangup");
    if (isEnteredRoom && adapterRef.current) {
      await adapterRef.current.sendMessage(CMD_ID_CALL, HANG_UP);
    }
    await handleHangup();
  }, [isEnteredRoom, handleHangup]);

  const handleTrackClick = useCallback(async () => {
    if (!adapterRef.current) return;

    await adapterRef.current.sendMessage(
      CMD_ID_TRACK,
      isTrackOn ? SWITCH_OFF : SWITCH_ON
    );
    setIsTrackOn(!isTrackOn);
  }, [isTrackOn]);

  const handleShowEmoji = useCallback(() => {
    setShowEmojiPanel(true);
  }, []);

  // 远程用户接入后开始计时，断开时停止计时
  useEffect(() => {
    if (remoteUser?.userId || remoteUser?.track) {
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          setCallSeconds((prev) => prev + 1);
        }, 1000);
      }
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setCallSeconds(0);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [remoteUser]);

  // 历史记录管理
  useEffect(() => {
    pushHistory();
    const confirm = (e: PopStateEvent) => {
      e.preventDefault();
      Dialog.show({
        content: "确定退出通话？",
        closeOnAction: true,
        actions: [
          [
            {
              key: "cancel",
              text: "取消",
              onClick: () => {
                window.history.pushState(null, "", window.location.href);
              },
            },
            {
              key: "delete",
              text: "退出通话",
              bold: true,
              danger: true,
              onClick: () => {
                handleHangup();
              },
            },
          ],
        ],
      });
    };
    addListener("popstate", confirm);

    return () => {
      removeListener("popstate", confirm);
    };
  }, [handleHangup]);

  // 清理
  useEffect(() => {
    const token = cancelToken.current;
    return () => {
      adapterRef.current?.exitRoom();
      token?.cancel(TOKEN_CANCEL);
      system.stopCallAudio();
    };
  }, []);

  return {
    // 状态
    role,
    status,
    isEnteredRoom,
    remoteUser,
    isVideoOpen,
    countdown,
    callSeconds,
    showEmojiPanel,
    useFrontCamera,
    isTrackOn,
    isFullScreen,
    formatTime,

    // 方法
    adapter: adapterRef.current,
    calling,
    handleVideoToggle,
    handleHangup: handleHangupClick,
    handleTrackClick,
    handleShowEmoji,
    setShowEmojiPanel,
    setUseFrontCamera,
    setIsFullScreen,
    renderVideoElements: (
      containerType: "remote" | "local",
      ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>,
      fullRef: React.RefObject<HTMLDivElement | HTMLVideoElement | null>,
      dragPosition?: { x: number; y: number },
      isDragging?: boolean,
      onDragStart?: (e: any) => void,
      onDragMove?: (e: any) => void,
      onDragEnd?: (e: any) => void,
      onChangeCamera?: () => void
    ) => {
      const res = adapterRef.current?.renderVideoElements(
        containerType,
        ref,
        fullRef,
        dragPosition,
        isDragging,
        onDragStart,
        onDragMove,
        onDragEnd,
        onChangeCamera
      );
      if (containerType === "remote") {
        remoteVideoRef.current = ref.current;
        remoteVideoFullRef.current = fullRef.current;
      } else {
        localVideoRef.current = ref.current;
      }
      return res;
    },
  };
};
